import { createFileRoute, Link } from "@tanstack/react-router";
import { getBooks, type PublicBook } from "@/lib/books.functions";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { breadcrumbs, itemList, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Bibliography | Books by Buddha KA";
const DESCRIPTION =
  "The complete Buddha KA bibliography — every book published through KAfrequency, with subjects, descriptions, and canonical links to each volume.";

export const Route = createFileRoute("/books/bibliography")({
  loader: async () => ({ books: await getBooks() }),
  component: Bibliography,
  head: ({ loaderData }) => {
    const books = loaderData?.books ?? [];
    return {
      ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/books/bibliography" }),
      links: [{ rel: "canonical", href: absoluteUrl("/books/bibliography") }],
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: `${SITE_URL}/books/bibliography#bibliography`,
            url: `${SITE_URL}/books/bibliography`,
            name: "Bibliography",
            description: DESCRIPTION,
            about: [personRef(), orgRef()],
            extra: {
              publisher: orgRef(),
              ...(books.length
                ? { mainEntity: itemList(books.map((b) => ({ path: `/books/${b.slug}`, name: b.title }))) }
                : {}),
            },
          }),
        ),
        ldScript(breadcrumbs([["Books", "/books"], ["Bibliography", "/books/bibliography"]])),
      ],
    };
  },
});

function Bibliography() {
  const { books } = Route.useLoaderData() as { books: PublicBook[] };

  return (
    <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Canonical Catalog</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl">Bibliography</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">{DESCRIPTION}</p>

      <ul className="mt-14 space-y-8">
        {books.map((b) => (
          <li key={b.slug} className="rounded-2xl border hairline bg-charcoal/40 p-6">
            <Link to="/books/$slug" params={{ slug: b.slug }} className="font-serif text-2xl hover:text-gold-luminous">
              {b.title}
            </Link>
            {b.subtitle && <p className="mt-1 text-ivory/80">{b.subtitle}</p>}
            <p className="mt-2 text-xs uppercase tracking-[0.2em] text-gold">
              {[b.author, b.category].filter(Boolean).join(" · ")}
            </p>
            {b.longDescription && (
              <p className="mt-3 line-clamp-4 text-sm text-muted-foreground">{b.longDescription}</p>
            )}
            {b.tags?.length ? (
              <p className="mt-3 text-xs text-muted-foreground">Subjects: {b.tags.join(", ")}</p>
            ) : null}
          </li>
        ))}
      </ul>

      {books.length === 0 && <p className="mt-16 text-muted-foreground">The bibliography is temporarily unavailable.</p>}
    </section>
  );
}
